import { apiClient } from "../../../lib/api-client";
import { VacancyDto } from "../dtos/VacancyDto";

export interface CandidateDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  location?: string;
  experienceYears?: number;
  education?: string;
  skills: string[];
  cvExtractionId?: string;
  createdAt: string;
  applicationCount: number;
}

export interface ApplicationDto {
  id: string;
  jobId: string;
  candidateId: string;
  organizationId: string;
  currentStage:
    | "Applied"
    | "PhoneScreen"
    | "Interview"
    | "TechnicalTest"
    | "Offer"
    | "Hired"
    | "Rejected"
    | "Applied"
    | "PhoneScreen"
    | "Interview"
    | "TechnicalTest"
    | "Offer"
    | "Hired"
    | "Rejected";
  appliedDate: string;
  stageChangedAt: string;
  matchScore: number;
  nextAction?: string;
  vacancy: VacancyDto;
  candidate: CandidateDto;
}

export interface CreateJobCommand {
  title: string;
  description: string;
  keyResponsibilities: string[];
  requiredSkills: string[];
  optionalSkills: string[];
  yearsOfExperience: number;
  educationLevel: string;
  certifications: string;
  organizationId?: string;
  seniorityLevel:
    | "Junior"
    | "Mid"
    | "Senior"
    | "Lead"
    | "Principal"
    | "Executive"
    | "Junior"
    | "Mid"
    | "Senior"
    | "Lead"
    | "Principal"
    | "Executive";
}

export interface UpdateJobCommand extends CreateJobCommand {
  id: string;
}

export interface SearchJobsQuery {
  searchTerm?: string;
  type?: "FullTime" | "PartTime" | "Contract" | "Internship";
  status?: "Active" | "Paused" | "Closed";
  locationType?: "Remote" | "Hybrid" | "OnSite";
  seniorityLevel?:
    | "Junior"
    | "Mid"
    | "Senior"
    | "Lead"
    | "Principal"
    | "Executive"
    | "Junior"
    | "Mid"
    | "Senior"
    | "Lead"
    | "Principal"
    | "Executive";
  department?: string;
  requiredSkills?: string[];
  minSalary?: number;
  maxSalary?: number;
  postedAfter?: string;
  postedBefore?: string;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface CandidateMatchDto {
  candidateId: string;
  candidateName: string;
  email: string;
  matchScore: number;
  skillsMatch: number;
  experienceMatch: number;
  matchedSkills: string[];
  missingSkills: string[];
  recommendation: string;
}

export interface JobMatchScoreDto {
  jobId: string;
  jobTitle: string;
  candidateId: string;
  candidateName: string;
  overallScore: number;
  skillScore: number;
  experienceScore: number;
  matchedSkills: string[];
  missingSkills: string[];
  recommendation: string;
  details: string;
}

export interface AiJdDraftRequest {
  prompt: string;
}

export interface AiJdDraftResponse {
  title: string;
  summary: string;
  responsibilities: string[];
  requirements: { mustHave: string[]; niceToHave: string[] };
  benefits?: string[];
  screeningQuestions: string[];
  rawText: string;
}

class RecruitmentService {
  async aiDraftJd(request: AiJdDraftRequest): Promise<AiJdDraftResponse> {
    const { data } = await apiClient.post("/ai/jd/draft", request);
    return data as AiJdDraftResponse;
  }

  async updateJobStatus(
    id: string,
    status: "Active" | "Paused" | "Closed"
  ): Promise<void> {
    await apiClient.put(`/job/${id}/status`, { id, status });
  }

  async deleteJobById(id: string): Promise<void> {
    await apiClient.delete(`/api/job/${id}`);
  }

  async getJobApplications(jobId: string): Promise<ApplicationDto[]> {
    const response = await apiClient.get(`/job/${jobId}/applications`);
    return response.data;
  }

  async matchCandidatesToJob(
    jobId: string,
    topN: number = 10
  ): Promise<CandidateMatchDto[]> {
    const response = await apiClient.get(`/job/${jobId}/match-candidates`, {
      params: { topN },
    });
    return response.data;
  }

  async calculateJobMatchScore(
    jobId: string,
    candidateId: string
  ): Promise<JobMatchScoreDto> {
    const response = await apiClient.get(`/job/${jobId}/match-score`, {
      params: { candidateId },
    });
    return response.data;
  }

  async getCandidates(searchTerm?: string): Promise<CandidateDto[]> {
    const response = await apiClient.get(`/candidate`, {
      params: { searchTerm },
    });
    return response.data;
  }

  async getCandidateById(id: string): Promise<CandidateDto> {
    const response = await apiClient.get(`/candidate/${id}`);
    return response.data;
  }

  async uploadCvAndCreateCandidate(
    file: File,
    folderId: string
  ): Promise<CandidateDto> {
    const formData = new FormData();
    formData.append("cvFile", file);
    formData.append("folderId", folderId);

    const response = await apiClient.post(`/candidate/upload-cv`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  async getApplicationsForVacancy(
    vacancyId?: string,
    stage?: string
  ): Promise<ApplicationDto[]> {
    const response = await apiClient.get(`/application`, {
      params: { vacancyId, stage },
    });
    return response.data;
  }

  async getApplicationById(id: string): Promise<ApplicationDto> {
    const response = await apiClient.get(`/application/${id}`);
    return response.data;
  }

  async createApplication(
    jobId: string,
    candidateId: string
  ): Promise<ApplicationDto> {
    const response = await apiClient.post(`/application`, {
      jobId,
      candidateId,
    });
    return response.data;
  }

  async updateApplicationStage(
    id: string,
    stage: string,
    notes?: string
  ): Promise<void> {
    await apiClient.put(`/application/${id}/stage`, {
      id,
      newStage: stage,
      notes,
    });
  }

  async deleteApplication(id: string): Promise<void> {
    await apiClient.delete(`/application/${id}`);
  }

  async getRecruitmentDashboard(organizationId: string) {
    const response = await apiClient.get(
      `/organizations/${organizationId}/recruitment/dashboard`
    );
    return response.data;
  }

  async getRecruitmentStats(
    startDate?: string,
    endDate?: string,
    organizationId?: string
  ) {
    const response = await apiClient.get(
      `/organizations/${organizationId}/recruitment/stats`,
      {
        params: { startDate, endDate },
      }
    );
    return response.data;
  }

  async getHiredCandidates(hiredAfter?: string, hiredBefore?: string) {
    const response = await apiClient.get(`/recruitment/hired`, {
      params: { hiredAfter, hiredBefore },
    });
    return response.data;
  }
}

export const recruitmentService = new RecruitmentService();
